/**
 * Sidebar icon for a manifest menu group or leaf link.
 *
 * - **String:** host SVG registry key (see `src/components/Base/Menu/iconRegistry.ts`).
 * - **Object:** federated **aga-components** `Icon` via `componentsIcon` / `componentsPath`, or a raster/SVG
 *   URL via `imageUrl` (prefer absolute `https://…` URLs to the remote’s static assets so the host resolves
 *   them correctly).
 *
 * Aligns with `@atlas-sp/aga-common-lib/module-federation-host` `ModuleManifestIcon` parsing.
 */
export type ModuleManifestIcon =
  | string
  | {
      componentsIcon?: string;
      componentsPath?: string;
      imageUrl?: string;
      alt?: string;
    };

function normalizeManifestIconString(raw: string): string | undefined {
  const trimmed = raw.trim();
  const withoutSvg = trimmed.replace(/\.svg$/i, '');
  const normalized = withoutSvg.toLowerCase();
  if (normalized.length === 0 || normalized === 'default') {
    return undefined;
  }
  return normalized;
}

/**
 * Parses a manifest `icon` value from JSON into **`ModuleManifestIcon`** or `undefined` if invalid/empty.
 *
 * @remarks
 * **Business Rules:** String keys are trimmed, optional `.svg` suffix removed, lowercased; `default` and empty → no icon.
 * For objects, `imageUrl` wins, then `componentsPath`, then `componentsIcon`. Extra keys are ignored.
 */
export function parseModuleManifestIcon(raw: unknown): ModuleManifestIcon | undefined {
  if (raw === undefined || raw === null) {
    return undefined;
  }
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (t.length === 0) {
      return undefined;
    }
    return normalizeManifestIconString(t);
  }
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined;
  }
  const o = raw as Record<string, unknown>;
  const imageUrl = typeof o.imageUrl === 'string' ? o.imageUrl.trim() : '';
  if (imageUrl.length > 0) {
    const alt = typeof o.alt === 'string' ? o.alt : undefined;
    return alt !== undefined ? { imageUrl, alt } : { imageUrl };
  }
  const componentsPath = typeof o.componentsPath === 'string' ? o.componentsPath : '';
  if (componentsPath.length > 0) {
    return { componentsPath };
  }
  const componentsIcon = typeof o.componentsIcon === 'string' ? o.componentsIcon.trim() : '';
  if (componentsIcon.length > 0) {
    return { componentsIcon };
  }
  return undefined;
}

/** Shared catalog fields for sidebar menu leaves and dashboard widget picker entries. */
export interface ModuleManifestCatalogItemBase {
  text: string;
  /** Optional short copy for tooltips, aside lists, or widget picker descriptions. */
  description?: string;
  permissions?: string[];
  permissionsMatch?: 'full' | 'partial';
  icon?: ModuleManifestIcon;
}

export interface ModuleManifestMenuItem extends ModuleManifestCatalogItemBase {
  /**
   * Leaf: required (link target under one of the manifest `hostRoutePrefix` values).
   * Branch (when `children` is non-empty): optional; omit for a label-only submenu group.
   */
  path?: string;
  /** Exposed federated component name; required for leaf items that navigate within the remote. */
  component?: string;
  activePath?: string;
  /** Nested submenu items (recursive). */
  children?: ModuleManifestMenuItem[];
  /** When true, nested group starts expanded once its parent section is open. */
  defaultExpanded?: boolean;
}

/** Built-in widget layout kinds; hosts may accept other strings for forward compatibility. */
export const KNOWN_MODULE_MANIFEST_WIDGET_TYPES = ['grid', 'graph', 'kpi', 'chart', 'table'] as const;

export type KnownModuleManifestWidgetType = (typeof KNOWN_MODULE_MANIFEST_WIDGET_TYPES)[number];

/** Widget layout / visualization kind (e.g. dashboard tiles in Titan). */
export type ModuleManifestWidgetType = KnownModuleManifestWidgetType | (string & {});

/**
 * Federated component exposed for placement on a host dashboard (e.g. Titan shell aside).
 * Shares catalog fields with menu leaves; `type` and `component` are required.
 */
export interface ModuleManifestWidgetItem extends ModuleManifestCatalogItemBase {
  type: ModuleManifestWidgetType;
  /** Exposed federated component name (must match `vite.config.ts` `exposes`, without `./`). */
  component: string;
  /** Optional deep link when the widget is activated; must be under `hostRoutePrefix` when set. */
  path?: string;
}

export interface ModuleManifestWidgets {
  section?: string;
  groupKey?: string;
  position?: number;
  items: ModuleManifestWidgetItem[];
}

export const isKnownModuleManifestWidgetType = (type: string): type is KnownModuleManifestWidgetType =>
  (KNOWN_MODULE_MANIFEST_WIDGET_TYPES as readonly string[]).includes(type);

export interface ModuleManifestRoute {
  path: string;
  component: string;
  layout?: 'logged-in' | 'none';
  auth?: boolean;
  /** When set, direct navigation to this route requires the same permission rules as host menu links. */
  permissions?: string[];
  permissionsMatch?: 'full' | 'partial';
  title?: string;
  /** When false, the host does not render breadcrumbs; the federated page owns the trail (e.g. dynamic detail titles). */
  hostBreadcrumbs?: boolean;
  breadcrumbs?: { text: string; href?: string }[];
}

/**
 * Public URL prefix for this remote in the host (no `/m/` segment).
 * Example: `/funding` for `funding-ui`. If omitted, derived from the federation remote name
 * (strip trailing `-ui`, then leading `aga-`, kebab-case → first segment).
 */
export type HostRoutePrefix = `/${string}`;

/** Single prefix or several when the same remote serves URLs under multiple top-level paths (e.g. `/funding/...` and `/configure/...`). */
export type HostRoutePrefixSpec = HostRoutePrefix | HostRoutePrefix[];

/** Sidebar link metadata on a `pages[]` entry. */
export interface ModuleManifestPageMenu {
  text: string;
  permissions?: string[];
  permissionsMatch?: 'full' | 'partial';
  icon?: ModuleManifestIcon;
  activePath?: string;
  children?: ModuleManifestMenuItem[];
  /** When true, nested group starts expanded once its parent section is open. */
  defaultExpanded?: boolean;
}

/** One routable page in the preferred `pages[]` authoring format. */
export interface ModuleManifestPage {
  path: string;
  component: string;
  layout?: 'logged-in' | 'none';
  auth?: boolean;
  title?: string;
  permissions?: string[];
  permissionsMatch?: 'full' | 'partial';
  /** When false, the host does not render breadcrumbs; the federated page owns the trail. */
  hostBreadcrumbs?: boolean;
  breadcrumbs?: { text: string; href?: string }[];
  menu?: ModuleManifestPageMenu;
}

export interface ModuleManifest {
  name: string;
  displayName: string;
  version: string;
  /**
   * Canonical host path prefix(es) for menu links and routes (e.g. `/funding` or `["/funding/my-app","/configure/my-app"]`).
   * When federation-config lists `hostRoutePrefix` for this remote, every prefix here must appear there so Next.js rewrites apply.
   */
  hostRoutePrefix?: HostRoutePrefixSpec;
  menu: {
    section?: string;
    icon?: ModuleManifestIcon;
    position?: number;
    /**
     * Stable id for merging multiple remotes into one sidebar group (same key → one parent, items concatenated).
     * If omitted, a normalized `section` (or `displayName`) is used so same label shares a group.
     */
    groupKey?: string;
    items: ModuleManifestMenuItem[];
  };
  /** Optional dashboard widgets for Vite hosts (e.g. Titan shell). Aggregated across remotes for a widget picker aside. */
  widgets?: ModuleManifestWidgets;
  routes: ModuleManifestRoute[];
  navigation?: {
    pathTransformations?: Record<string, string>;
  };
}

/**
 * Mutates **`manifest.menu`** icons in place: **`menu.icon`** and every **`menu.items[].icon`** (including nested **`children`**).
 *
 * @remarks
 * **Business Rules:** Invalid values are removed; **`console.warn`** includes `remoteName` and a field path for diagnostics.
 */
export function sanitizeModuleManifestMenuIcons(manifest: ModuleManifest, remoteName: string): void {
  const sanitizeDeclaredIcon = (value: unknown, path: string): ModuleManifestIcon | undefined => {
    const parsed = parseModuleManifestIcon(value);
    if (value !== undefined && value !== null && parsed === undefined) {
      console.warn(`[ManifestLoader] ${remoteName}: invalid icon at ${path}, ignored`);
    }
    return parsed;
  };

  const sanitizeMenuItemIcons = (items: ModuleManifestMenuItem[], basePath: string): void => {
    items.forEach((item, index) => {
      const itemPath = `${basePath}[${index}]`;
      if ('icon' in item && item.icon !== undefined) {
        const next = sanitizeDeclaredIcon(item.icon, `${itemPath}.icon`);
        if (next !== undefined) {
          item.icon = next;
        } else {
          delete item.icon;
        }
      }
      if (item.children?.length) {
        sanitizeMenuItemIcons(item.children, `${itemPath}.children`);
      }
    });
  };

  if (manifest.menu.icon !== undefined) {
    const next = sanitizeDeclaredIcon(manifest.menu.icon, 'menu.icon');
    if (next !== undefined) {
      manifest.menu.icon = next;
    } else {
      delete manifest.menu.icon;
    }
  }
  sanitizeMenuItemIcons(manifest.menu.items, 'menu.items');
}

/**
 * Default public path prefix when `hostRoutePrefix` is not set in the manifest.
 * Examples: `funding-ui` → `/funding`, `aga-dealcard` → `/dealcard`, `document-manager-ui` → `/document-manager`.
 */
/** Normalizes menu section labels so remotes can share a group without identical spelling. */
export function normalizeMenuGroupKey(label: string): string {
  return label.trim().toLowerCase().replace(/\s+/g, ' ');
}

export function deriveHostRoutePrefixFromRemoteName(remoteName: string): HostRoutePrefix {
  let base = remoteName.trim();
  if (base.endsWith('-ui')) {
    base = base.slice(0, -3);
  }
  if (base.startsWith('aga-')) {
    base = base.slice(4);
  }
  base = base.replace(/^\/+|\/+$/g, '');
  if (!base) {
    base = 'module';
  }
  return `/${base}`;
}

const normalizeHostRoutePrefixToken = (value: string): HostRoutePrefix | null => {
  const t = value.trim().replace(/\/$/, '');
  if (!t.startsWith('/') || t.length < 2) {
    return null;
  }
  return t as HostRoutePrefix;
};

/**
 * Parses manifest `hostRoutePrefix` (missing, string, or string[]) into a non-empty prefix list, or null if invalid.
 * When `raw` is missing, returns the single prefix derived from `remoteName`.
 */
export function parseAndResolveHostRoutePrefixes(raw: unknown, remoteName: string): HostRoutePrefix[] | null {
  if (raw === undefined || raw === null) {
    return [deriveHostRoutePrefixFromRemoteName(remoteName)];
  }

  if (typeof raw === 'string') {
    const one = normalizeHostRoutePrefixToken(raw);
    if (!one) {
      return null;
    }
    return [one];
  }

  if (!Array.isArray(raw)) {
    return null;
  }

  const out: HostRoutePrefix[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (typeof item !== 'string') {
      return null;
    }
    const c = normalizeHostRoutePrefixToken(item);
    if (!c) {
      return null;
    }
    if (!seen.has(c)) {
      seen.add(c);
      out.push(c);
    }
  }

  if (out.length === 0) {
    return null;
  }
  return out;
}

/** Returns true when `path` equals `prefix` or is nested under `prefix/`. */
export const pathUnderHostRoutePrefix = (path: string, prefix: HostRoutePrefix): boolean =>
  path === prefix || path.startsWith(`${prefix}/`);

/** True if `path` is under at least one of the given prefixes. */
export const pathUnderAnyHostRoutePrefix = (path: string, prefixes: readonly HostRoutePrefix[]): boolean =>
  prefixes.some(prefix => pathUnderHostRoutePrefix(path, prefix));

/**
 * Canonical prefix list for a validated manifest (string or array form).
 * Call only after `hostRoutePrefix` has been validated; falls back to derivation when unset.
 */
export function getManifestHostRoutePrefixes(manifest: ModuleManifest, remoteName: string): HostRoutePrefix[] {
  return (
    parseAndResolveHostRoutePrefixes(manifest.hostRoutePrefix, remoteName) ?? [
      deriveHostRoutePrefixFromRemoteName(remoteName),
    ]
  );
}

/**
 * Unique `/${firstSegment}` prefixes from page paths (sorted).
 */
export function deriveHostRoutePrefixesFromPagePaths(paths: readonly string[]): HostRoutePrefix[] {
  const seen = new Set<string>();
  const out: HostRoutePrefix[] = [];
  for (const rawPath of paths) {
    const normalized = rawPath.trim().replace(/\/{2,}/g, '/');
    const segments = normalized.split('/').filter(Boolean);
    if (segments.length === 0) {
      continue;
    }
    const prefix = `/${segments[0]}` as HostRoutePrefix;
    if (!seen.has(prefix)) {
      seen.add(prefix);
      out.push(prefix);
    }
  }
  out.sort();
  return out;
}

const pageMenuToMenuItem = (
  menuRaw: Record<string, unknown>,
  path: string,
  component: string,
): ModuleManifestMenuItem | null => {
  if (typeof menuRaw.text !== 'string' || menuRaw.text.trim().length === 0) {
    return null;
  }
  const item: ModuleManifestMenuItem = {
    text: menuRaw.text.trim(),
    path,
    component,
  };
  if (Array.isArray(menuRaw.permissions)) {
    item.permissions = menuRaw.permissions.filter((p): p is string => typeof p === 'string');
  }
  if (menuRaw.permissionsMatch === 'full' || menuRaw.permissionsMatch === 'partial') {
    item.permissionsMatch = menuRaw.permissionsMatch;
  }
  const parsedIcon = parseModuleManifestIcon(menuRaw.icon);
  if (parsedIcon !== undefined) {
    item.icon = parsedIcon;
  }
  if (typeof menuRaw.activePath === 'string' && menuRaw.activePath.trim().length > 0) {
    item.activePath = menuRaw.activePath.trim();
  }
  if (menuRaw.defaultExpanded === true) {
    item.defaultExpanded = true;
  }
  if (Array.isArray(menuRaw.children)) {
    const children: ModuleManifestMenuItem[] = [];
    for (const childRaw of menuRaw.children) {
      if (!childRaw || typeof childRaw !== 'object' || Array.isArray(childRaw)) {
        continue;
      }
      const child = childRaw as Record<string, unknown>;
      const childPath =
        typeof child.path === 'string' && child.path.trim().length > 0
          ? child.path.trim().replace(/\/{2,}/g, '/')
          : path;
      const childComponent =
        typeof child.component === 'string' && child.component.trim().length > 0 ? child.component.trim() : component;
      const childMenu = pageMenuToMenuItem(child, childPath, childComponent);
      if (childMenu) {
        children.push(childMenu);
      }
    }
    if (children.length > 0) {
      item.children = children;
    }
  }
  return item;
};

/**
 * When a page declares `permissions` for the route guard but a menu leaf omits them,
 * inherit page permissions onto that leaf so hosts hide the item (empty permissions = public).
 * Groups stay permission-free; only navigable leaves are gated.
 */
function applyPagePermissionFallbackToMenuLeaves(
  item: ModuleManifestMenuItem,
  pagePermissions: string[] | undefined,
  pageMatch: 'full' | 'partial' | undefined,
): void {
  if (item.children?.length) {
    for (const child of item.children) {
      applyPagePermissionFallbackToMenuLeaves(child, pagePermissions, pageMatch);
    }
    return;
  }

  const existing = (item.permissions ?? []).filter(
    (permission): permission is string => typeof permission === 'string' && permission.trim().length > 0,
  );
  if (!existing.length && pagePermissions?.length) {
    item.permissions = [...pagePermissions];
  }
  if (!item.permissionsMatch && (pageMatch === 'full' || pageMatch === 'partial')) {
    item.permissionsMatch = pageMatch;
  }
}

/**
 * Expands `pages[]` authoring into legacy `menu.items`, `routes`, and `hostRoutePrefix`.
 */
export function expandManifestFromPages(
  raw: Record<string, unknown>,
  remoteName: string,
): Record<string, unknown> | null {
  const pagesRaw = raw.pages;
  if (pagesRaw === undefined || pagesRaw === null) {
    return null;
  }
  if (!Array.isArray(pagesRaw) || pagesRaw.length === 0) {
    console.warn(`[ManifestLoader] ${remoteName}: pages must be a non-empty array`);
    return null;
  }
  if (Array.isArray(raw.routes)) {
    console.warn(`[ManifestLoader] ${remoteName}: manifest cannot define both pages and routes`);
    return null;
  }

  const menuObj = raw.menu;
  if (!menuObj || typeof menuObj !== 'object' || Array.isArray(menuObj)) {
    console.warn(`[ManifestLoader] ${remoteName}: manifest missing menu when using pages`);
    return null;
  }
  const menuRecord = menuObj as Record<string, unknown>;
  if (Array.isArray(menuRecord.items) && menuRecord.items.length > 0) {
    console.warn(`[ManifestLoader] ${remoteName}: manifest cannot define both pages and menu.items`);
    return null;
  }

  const routes: ModuleManifestRoute[] = [];
  const menuItems: ModuleManifestMenuItem[] = [];
  const pagePaths: string[] = [];

  for (let i = 0; i < pagesRaw.length; i++) {
    const pageRaw = pagesRaw[i];
    const label = `pages[${i}]`;
    if (!pageRaw || typeof pageRaw !== 'object' || Array.isArray(pageRaw)) {
      console.warn(`[ManifestLoader] ${remoteName}: ${label} must be an object`);
      return null;
    }
    const page = pageRaw as Record<string, unknown>;
    if (typeof page.path !== 'string' || typeof page.component !== 'string') {
      console.warn(`[ManifestLoader] ${remoteName}: ${label} requires path and component`);
      return null;
    }
    const path = page.path.trim().replace(/\/{2,}/g, '/');
    const component = page.component.trim();
    pagePaths.push(path);

    const route: ModuleManifestRoute = { path, component };
    if (page.layout === 'logged-in' || page.layout === 'none') {
      route.layout = page.layout;
    }
    if (typeof page.auth === 'boolean') {
      route.auth = page.auth;
    }
    if (typeof page.title === 'string' && page.title.trim().length > 0) {
      route.title = page.title.trim();
    }
    if (Array.isArray(page.permissions)) {
      route.permissions = page.permissions.filter((p): p is string => typeof p === 'string');
    }
    if (page.permissionsMatch === 'full' || page.permissionsMatch === 'partial') {
      route.permissionsMatch = page.permissionsMatch;
    }
    if (page.hostBreadcrumbs === false) {
      route.hostBreadcrumbs = false;
    }
    if (Array.isArray(page.breadcrumbs)) {
      route.breadcrumbs = page.breadcrumbs
        .filter(
          (b): b is { text: string; href?: string } =>
            Boolean(b) &&
            typeof b === 'object' &&
            !Array.isArray(b) &&
            typeof (b as { text?: unknown }).text === 'string' &&
            (b as { text: string }).text.trim().length > 0,
        )
        .map(b => ({
          text: (b as { text: string }).text.trim(),
          ...((b as { href?: unknown }).href !== undefined &&
          typeof (b as { href?: unknown }).href === 'string' &&
          (b as { href: string }).href.trim().length > 0
            ? { href: (b as { href: string }).href.trim() }
            : {}),
        }));
    }
    routes.push(route);

    if (page.menu !== undefined && page.menu !== null) {
      if (typeof page.menu !== 'object' || Array.isArray(page.menu)) {
        console.warn(`[ManifestLoader] ${remoteName}: ${label}.menu must be an object`);
        return null;
      }
      const menuItem = pageMenuToMenuItem(page.menu as Record<string, unknown>, path, component);
      if (!menuItem) {
        console.warn(`[ManifestLoader] ${remoteName}: ${label}.menu missing text`);
        return null;
      }
      applyPagePermissionFallbackToMenuLeaves(menuItem, route.permissions, route.permissionsMatch);
      menuItems.push(menuItem);
    }
  }

  const derivedPrefixes = deriveHostRoutePrefixesFromPagePaths(pagePaths);
  if (derivedPrefixes.length === 0) {
    console.warn(`[ManifestLoader] ${remoteName}: pages produced no hostRoutePrefix`);
    return null;
  }

  let hostRoutePrefix: HostRoutePrefixSpec;
  if (raw.hostRoutePrefix !== undefined && raw.hostRoutePrefix !== null) {
    const explicit = parseAndResolveHostRoutePrefixes(raw.hostRoutePrefix, remoteName);
    if (!explicit) {
      console.warn(`[ManifestLoader] ${remoteName}: invalid hostRoutePrefix`);
      return null;
    }
    for (const p of pagePaths) {
      if (!pathUnderAnyHostRoutePrefix(p, explicit)) {
        console.warn(`[ManifestLoader] ${remoteName}: page path "${p}" is not under declared hostRoutePrefix`);
        return null;
      }
    }
    hostRoutePrefix = explicit.length === 1 ? explicit[0] : explicit;
  } else {
    hostRoutePrefix = derivedPrefixes.length === 1 ? derivedPrefixes[0] : derivedPrefixes;
  }

  const expanded: Record<string, unknown> = { ...raw };
  delete expanded.pages;
  expanded.hostRoutePrefix = hostRoutePrefix;
  expanded.menu = { ...menuRecord, items: menuItems };
  expanded.routes = routes;
  return expanded;
}

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;

const parseCatalogItemBase = (
  raw: Record<string, unknown>,
  itemLabel: string,
  remoteName: string,
): Pick<ModuleManifestCatalogItemBase, 'text' | 'description' | 'permissions' | 'permissionsMatch'> | null => {
  if (!isNonEmptyString(raw.text)) {
    console.warn(`[ManifestLoader] ${remoteName}: ${itemLabel} missing text`);
    return null;
  }
  const base: Pick<ModuleManifestCatalogItemBase, 'text' | 'description' | 'permissions' | 'permissionsMatch'> = {
    text: raw.text.trim(),
  };
  if (raw.description !== undefined) {
    if (!isNonEmptyString(raw.description)) {
      console.warn(`[ManifestLoader] ${remoteName}: ${itemLabel} description must be a non-empty string, ignored`);
    } else {
      base.description = raw.description.trim();
    }
  }
  if (Array.isArray(raw.permissions)) {
    base.permissions = raw.permissions.filter((p): p is string => typeof p === 'string');
  }
  if (raw.permissionsMatch === 'full' || raw.permissionsMatch === 'partial') {
    base.permissionsMatch = raw.permissionsMatch;
  }
  return base;
};

/**
 * Parses optional `widgets` from manifest JSON. Returns `undefined` when absent; `null` when invalid.
 */
export function parseModuleManifestWidgets(
  raw: unknown,
  remoteName: string,
  pathOk?: (path: string) => boolean,
): ModuleManifestWidgets | undefined | null {
  if (raw === undefined || raw === null) {
    return undefined;
  }
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    console.warn(`[ManifestLoader] ${remoteName}: widgets must be an object`);
    return null;
  }
  const block = raw as Record<string, unknown>;
  if (!Array.isArray(block.items)) {
    console.warn(`[ManifestLoader] ${remoteName}: widgets.items must be an array`);
    return null;
  }

  const widgets: ModuleManifestWidgets = { items: [] };

  if (block.section !== undefined) {
    if (!isNonEmptyString(block.section)) {
      console.warn(`[ManifestLoader] ${remoteName}: widgets.section must be a non-empty string, ignored`);
    } else {
      widgets.section = block.section.trim();
    }
  }
  if (block.groupKey !== undefined) {
    if (!isNonEmptyString(block.groupKey)) {
      console.warn(`[ManifestLoader] ${remoteName}: widgets.groupKey must be a non-empty string, ignored`);
    } else {
      widgets.groupKey = block.groupKey.trim();
    }
  }
  if (block.position !== undefined) {
    if (typeof block.position !== 'number' || !Number.isFinite(block.position)) {
      console.warn(`[ManifestLoader] ${remoteName}: widgets.position must be a number, ignored`);
    } else {
      widgets.position = block.position;
    }
  }

  for (let i = 0; i < block.items.length; i++) {
    const itemRaw = block.items[i];
    const itemLabel = `widgets.items[${i}]`;
    if (!itemRaw || typeof itemRaw !== 'object' || Array.isArray(itemRaw)) {
      console.warn(`[ManifestLoader] ${remoteName}: ${itemLabel} must be an object`);
      return null;
    }
    const itemObj = itemRaw as Record<string, unknown>;
    const base = parseCatalogItemBase(itemObj, itemLabel, remoteName);
    if (!base) {
      return null;
    }
    if (!isNonEmptyString(itemObj.type)) {
      console.warn(`[ManifestLoader] ${remoteName}: ${itemLabel} missing type`);
      return null;
    }
    if (!isNonEmptyString(itemObj.component)) {
      console.warn(`[ManifestLoader] ${remoteName}: ${itemLabel} missing component`);
      return null;
    }
    const widgetType = itemObj.type.trim() as ModuleManifestWidgetType;
    if (!isKnownModuleManifestWidgetType(widgetType)) {
      console.warn(
        `[ManifestLoader] ${remoteName}: ${itemLabel} type "${widgetType}" is not a built-in kind ` +
          `(${KNOWN_MODULE_MANIFEST_WIDGET_TYPES.join(', ')}); hosts may still render it`,
      );
    }
    const widget: ModuleManifestWidgetItem = {
      ...base,
      type: widgetType,
      component: itemObj.component.trim(),
    };
    if (itemObj.path !== undefined) {
      if (!isNonEmptyString(itemObj.path)) {
        console.warn(`[ManifestLoader] ${remoteName}: ${itemLabel} path must be a non-empty string`);
        return null;
      }
      const normalizedPath = itemObj.path.trim().replace(/\/{2,}/g, '/');
      if (pathOk && !pathOk(normalizedPath)) {
        console.warn(
          `[ManifestLoader] ${remoteName}: ${itemLabel} path "${normalizedPath}" must equal one hostRoutePrefix or be nested under it`,
        );
        return null;
      }
      widget.path = normalizedPath;
    }
    widgets.items.push(widget);
  }

  return widgets;
}

export interface LoadedModuleManifest {
  remoteName: string;
  manifest: ModuleManifest;
  baseUrl: string;
  loadedAt: number;
  healthy: boolean;
}
